/** Hosting: every site the workspace hosts (its Weavex website and external sites), with progress. */
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { portal } from "./api";
import type { CustomDomain, Deployment, Site } from "./types";

export interface HostedSite {
  site: Site;
  /** Newest first. */
  deployments: Deployment[];
  domains: CustomDomain[];
}

async function load(siteId: string): Promise<Omit<HostedSite, "site">> {
  const [deployments, domains] = await Promise.all([portal.deployments(siteId), portal.domains(siteId)]);
  return { deployments: deployments.deployments, domains: domains.domains };
}

export function useHosting() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: ["hosting", orgId],
    queryFn: async () => {
      const { sites } = await portal.sites(orgId);
      return Promise.all(sites.map(async (site) => ({ site, ...(await load(site.id)) })));
    },
    // Keep a deploying site's status current on the overview.
    refetchInterval: (q) =>
      q.state.data?.some((h) => h.deployments.length > 0 && !isSettled(h)) ? 5000 : false,
  });
}

/** One hosted site, polled while its deployment or domain is still settling. */
export function useHostedSite(siteId: string) {
  return useQuery({
    queryKey: ["hosted-site", siteId],
    queryFn: async (): Promise<HostedSite> => {
      const site = await portal.site(siteId);
      return { site, ...(await load(siteId)) };
    },
    refetchInterval: (q) => (q.state.data && isSettled(q.state.data) ? false : 5000),
  });
}

/** Bytes kept for every deployment of every site, which is what the plan's storage covers. */
export const storageUsed = (sites: HostedSite[]) =>
  sites.reduce((n, s) => n + s.deployments.reduce((m, d) => m + d.size_bytes, 0), 0);

export const isLive = (s: Site) => s.status === "active" && s.live_deployment_id !== null;

export type StepState = "done" | "active" | "waiting" | "failed";
export interface Step {
  title: string;
  detail: string;
  state: StepState;
  /** Pill text. */
  label: string;
}

/** The deployment progress list (Figma "Hosting — Deploying · awaiting DNS & TLS"). */
export function deploymentSteps(
  { site, deployments, domains }: HostedSite,
  clock: (iso: string | null) => string,
): Step[] {
  const latest = deployments[0];
  const domain = domains[0];
  const steps: Step[] = [];

  if (!latest) {
    steps.push({
      title: "Files uploaded and checked",
      detail: "Waiting for the upload",
      state: "active",
      label: "Uploading",
    });
  } else {
    steps.push({
      title: "Files uploaded and checked",
      detail: `${latest.file_count.toLocaleString()} files · ${clock(latest.created_at)}`,
      state: "done",
      label: "Done",
    });
  }

  const built = latest?.status === "ready";
  if (latest?.status === "failed")
    steps.push({
      title: "Site deployed",
      detail: latest.error_message ?? "The deployment failed",
      state: "failed",
      label: "Failed",
    });
  else
    steps.push({
      title: "Site deployed",
      detail: built
        ? `Running on CortexGrip hosting · ${clock(latest.finished_at)}`
        : "Publishing your files",
      state: built ? "done" : latest ? "active" : "waiting",
      label: built ? "Done" : latest ? "Deploying" : "Waiting",
    });

  if (domain) {
    const connected = domain.status === "active";
    const failed = domain.status === "failed";
    steps.push({
      title: `Connecting ${domain.hostname}`,
      detail: connected
        ? `Connected · ${clock(domain.verified_at)}`
        : failed
          ? (domain.check_error ?? "We couldn’t find the DNS record")
          : "Waiting for the DNS record to spread. This can take up to a few hours.",
      state: connected ? "done" : failed ? "failed" : built ? "active" : "waiting",
      label: connected ? "Done" : failed ? "Not found" : "Awaiting DNS",
    });
    steps.push({
      title: "Security certificate (TLS)",
      detail: connected ? "Issued automatically" : "Issued automatically once DNS is confirmed",
      state: connected ? "done" : "waiting",
      label: connected ? "Done" : "Waiting",
    });
  }

  const live = isLive(site) && built && (!domain || domain.status === "active");
  steps.push({
    title: "Live",
    detail: live
      ? `Serving at ${domain?.hostname ?? site.hostname}`
      : "Your site goes live when every step above is confirmed",
    state: live ? "done" : "waiting",
    label: live ? "Live" : "Waiting",
  });
  return steps;
}

/** Nothing left to wait for: stop polling. */
export function isSettled(h: HostedSite): boolean {
  const latest = h.deployments[0];
  const deployDone = latest?.status === "ready" || latest?.status === "failed";
  const domainDone = h.domains.every((d) => d.status !== "pending_verification");
  return Boolean(latest) && deployDone && domainDone;
}
